import { FC, useCallback, useEffect, useState } from 'react'
import { Divider } from 'antd'
import { CenteredSpin, Pagination, Empty } from '../ui'
import { getGenerators } from '../../store/generators'
import style from './style.m.less'
import { useTranslation } from 'react-i18next'
import { ESortType } from './components/Controls/Controls'
import debounce from 'lodash/debounce'
import request from '../../services/request'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { getDictionaries } from '../../store/dictionaries'
import { ELoading } from '../../store/dictionaries/interfaces'
import { getUserInfo } from '../../store/user'
import { Filters, Controls, GeneratorCard, ControlsAdmin } from './components'
import moment from 'moment'
import { IFilterValue } from './components/Filters/Filters'
import { EGoStatusType, EUserRole } from '../../constants/approval'
import { IGeneratingObject } from './components/GeneratorCard/GeneratorCard'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { EIsActive } from './components/ControlsAdmin/ControlsAdmin'
import {useFilters} from "../hooks";

const PAGE_SIZE = 10

interface IData {
    total: number
    gos_registry?: IGeneratingObject[]
    power_max: number
}

interface IGenerators {
    isAuth: boolean
}

const defaultFilters = {
    renewableEnergyType: [],
    totalPowerFrom: 0,
    totalPowerTo: 0,
    qualificationDate: null,
    lastCheckDate: null,
    regionType: 0,
    statusType: EGoStatusType.All,
}

const Generators: FC<IGenerators> = ({ isAuth }) => {
    const { t } = useTranslation()
    const location = useLocation()

    const [page, setPage] = useState<number>(() => {
        if (localStorage.getItem('generators_page')) {
            return Number(localStorage.getItem('generators_page'))
        } else {
            localStorage.setItem('generators_page', '1')
            return 1
        }
    })
    const [pageSize, setPageSize] = useState<number>(() => {
        if (localStorage.getItem('generators_page_size')) {
            return Number(localStorage.getItem('generators_page_size'))
        } else {
            localStorage.setItem('generators_page_size', String(PAGE_SIZE))
            return PAGE_SIZE
        }
    })

    useEffect(() => {
        if (isAuth) {
            dispatch(getGenerators({ page }))
        }
    }, [page])

    const [data, setData] = useState<IData>()
    const [loading, setLoading] = useState(true)

    const { isactive: isActive = EIsActive.Active } = useParams<{ isactive?: EIsActive }>()
    const navigate = useNavigate()
    const [userNameFilter, setUserNameFilter] = useState(() => {
        if (localStorage.getItem('generators_user_name')) {
            return localStorage.getItem('generators_user_name')
        } else {
            localStorage.setItem('generators_user_name', '')
            return ''
        }
    })
    const [sortType, setSortType] = useState<ESortType>()
    const [descending, setDescending] = useState<boolean>()
    const [filters, setFilters] = useFilters()


    const loadOwners = useCallback(
        async (
            page: number,
            pageSize: number,
            filters: IFilterValue,
            name?: string,
            type_id?: ESortType,
            is_descending?: boolean,
            isActive?: EIsActive,
        ) => {
            try {
                setLoading(true)
                const res = await request.post(`/api/generators/v1/gos-registry`, {
                    limit: pageSize,
                    offset: (page - 1) * pageSize,
                    filters: {
                        is_active: isActive === EIsActive.Active,
                        name,
                        renewable_energy_type_ids: filters.renewableEnergyType,
                        power_from: filters.totalPowerFrom,
                        power_to: filters.totalPowerTo,
                        qualification_date_from: filters.qualificationDate
                            ? moment(filters.qualificationDate[0]).startOf('day')
                            : undefined,
                        qualification_date_to: filters.qualificationDate
                            ? moment(filters.qualificationDate[1]).endOf('day')
                            : undefined,
                        last_check_date_from: filters.lastCheckDate
                            ? moment(filters.lastCheckDate[0]).startOf('day')
                            : undefined,
                        last_check_date_to: filters.lastCheckDate
                            ? moment(filters.lastCheckDate[1]).endOf('day')
                            : undefined,
                        region_id: filters.regionType,
                        has_blocks:
                            filters.statusType === EGoStatusType.Blocked
                                ? true
                                : filters.statusType === EGoStatusType.NotBlocked
                                ? false
                                : null,
                    },
                    sort: {
                        type_id,
                        is_descending,
                    },
                })
                setData(res.data)
            } finally {
                setLoading(false)
            }
        },
        [],
    )

    const loadOwnersDebounced = useCallback(debounce(loadOwners, 300), [])

    useEffect(() => {
        loadOwnersDebounced(page, pageSize, filters, userNameFilter, sortType, descending, isActive)

        if (location.state && location.state.fromMenu) {
            setFilters(defaultFilters)
            setUserNameFilter('')
            localStorage.setItem('generators_user_name', '')
            setPage(1)
            localStorage.setItem('generators_page', '1')
            setPageSize(PAGE_SIZE)
            localStorage.setItem('generators_page_size', String(PAGE_SIZE))

            navigate(location.pathname, {state: {fromMenu: false}})
        }
    }, [page, pageSize, filters, userNameFilter, sortType, descending])

    const isLoading = useAppSelector((state) => state.dictionaries.loading)

    const dispatch = useAppDispatch()
    useEffect(() => {
        if (isLoading !== ELoading.Loaded) {
            dispatch(getDictionaries())
        }
        if (isAuth) {
            dispatch(getUserInfo())
        }
    }, [])

    const userRole: EUserRole = isAuth && useAppSelector((state) => state.user.data.user_role)

    return (
        <div className={style.generatorsMain}>
            {(loading || isLoading !== ELoading.Loaded) && <CenteredSpin />}
            <div className={style.mainHeader}>{t('generators.reestr')}</div>
            <div className={style.mainWrapper}>
                {userRole === EUserRole.Administrator ||
                userRole === EUserRole.SuperAdmin ||
                userRole === EUserRole.Operator ? (
                    <ControlsAdmin
                        isActive={isActive}
                        filters={filters}
                        onChangeActive={(isActive) => {
                            loadOwners(
                                page,
                                pageSize,
                                filters,
                                userNameFilter,
                                sortType,
                                descending,
                                isActive,
                            )
                            setPage(1)
                            localStorage.setItem('generators_page', '1')
                            setPageSize(PAGE_SIZE)
                            localStorage.setItem('generators_page_size', String(PAGE_SIZE))
                            navigate(`/generators/${isActive}`)
                        }}
                    />
                ) : (
                    <Controls
                        name={userNameFilter}
                        sortType={sortType}
                        desc={descending}
                        onChangeUserName={(name) => {
                            setUserNameFilter(name)
                            localStorage.setItem('generators_user_name', name)
                            setPage(1)
                            localStorage.setItem('generators_page', '1')
                            setPageSize(PAGE_SIZE)
                            localStorage.setItem('generators_page_size', String(PAGE_SIZE))
                        }}
                        onChangeSortType={setSortType}
                        onDescendingType={setDescending}
                    />
                )}
                <div className={style.mainBlock}>
                    <div className={style.cardsBlock}>
                        {isLoading === ELoading.Loaded &&
                            (data?.gos_registry && data?.gos_registry?.length > 0 ? (
                                (data?.gos_registry || []).map((item, index) => {
                                    const { id } = item
                                    return (
                                        <div key={id}>
                                            <GeneratorCard generatorData={item} />
                                            {index !== data.gos_registry.length - 1 && <Divider />}
                                        </div>
                                    )
                                })
                            ) : (
                                <Empty
                                    imageStyle={{
                                        height: 60,
                                        marginTop: 60,
                                    }}
                                    descriptionText={t('ownerCard.errorNothingFound')}
                                />
                            ))}
                    </div>
                    <Filters
                        values={filters}
                        onChange={(values) => {
                            setFilters(values)
                            setPage(1)
                            localStorage.setItem('generators_page', '1')
                            setPageSize(PAGE_SIZE)
                            localStorage.setItem('generators_page_size', String(PAGE_SIZE))
                        }}
                        name={userNameFilter}
                        onChangeUserName={(name) => {
                            setUserNameFilter(name)
                            localStorage.setItem('generators_user_name', name)
                            setPage(1)
                            localStorage.setItem('generators_page', '1')
                            setPageSize(PAGE_SIZE)
                            localStorage.setItem('generators_page_size', String(PAGE_SIZE))
                        }}
                        isAuth={isAuth}
                    />
                </div>
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    paginationChange={(receivedPage: number, pageSize?: number) => {
                        setPage(receivedPage)
                        localStorage.setItem('generators_page', String(receivedPage))
                        if (pageSize) {
                            setPageSize(pageSize)
                            localStorage.setItem('generators_page_size', String(pageSize))
                        }
                    }}
                    total={data?.total}
                    showSizeChanger
                />
            </div>
        </div>
    )
}

export default Generators
